<template>
    <Head title="Offices"/>

    <h1 class="mt-2 text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
        Offices
    </h1>

    <div class="bg-white py-6 px-4 mt-4 sm:p-6 shadow">
        <div class="flex justify-end">
            <search-filter :show-filter="false" v-model:value="form.search" class="w-1/4 max-w-md mr-4"
                           @reset="reset"></search-filter>
            <Button v-on:click="createOfficeSlideout()"
                    class="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4 rounded-md text-white bg-amaze-coral focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-amaze-coral">
                <svg class="-ml-0.5 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"
                     stroke="currentColor" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                          d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                </svg>
                Add New Office
            </Button>
        </div>
        <div class="flex flex-col mt-3 space-y-3">
            <div class="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
                <div class="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                    <div class="shadow overflow-hidden border-b border-gray-200 sm:rounded-lg">
                        <table class="min-w-full divide-y divide-gray-200">
                            <thead class="bg-gray-50">
                            <tr>
                                <th scope="col"
                                    class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                    Name
                                </th>
                                <th scope="col"
                                    class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                    Unit4 Code
                                </th>
                                <th scope="col"
                                    class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                    Email
                                </th>
                                <th scope="col"
                                    class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                    Telephone
                                </th>
                                <th scope="col"
                                    class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                    Mobile
                                </th>
                                <th scope="col"
                                    class="px-6 py-3 text-left text-xs text-gray-500 uppercase tracking-wider">
                                    Website
                                </th>
                                <th scope="col"
                                    class="px-6 py-3 text-left text-xs text-center text-gray-500 uppercase tracking-wider">
                                    Bank Accounts
                                </th>
                                <th scope="col" class="relative px-6 py-3">
                                    <span class="sr-only">Edit</span>
                                </th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr v-if="offices.data.length > 0" v-for="(office, index) in offices.data" :key="index"
                                :class="{'bg-gray-100': index % 2}" class="hover:bg-gray-50 cursor-pointer">
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {{ office.name }}
                                    <div class="text-sm text-gray-500">
                                        {{ office.short_name }}
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {{ office.unit4_code || '—' }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {{ office.email }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {{ office.telephone }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {{ office.mobile }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                    {{ office.website }}
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-center text-gray-500">
                                    <div class="w-full flex justify-center items-center gap-3">

                                        <span class="underline text-pitch-purple" @click="editOfficeBankAccountsSlideout(office)">{{
                                                Object.keys(office.bank_accounts).length
                                            }}</span>
                                        <button @click="editOfficeBankAccountsSlideout(office)" type="button"
                                                class="inline-flex items-center p-1 border border-transparent rounded-full shadow-sm text-white bg-amaze-coral hover:bg-amaze-coral focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
                                            <PlusSmIconSolid class="h-4 w-4 text-white" aria-hidden="true"/>
                                        </button>
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-right text-sm justify-end">
                                    <PencilAltIcon @click="editOfficeSlideout(office)"
                                                   class="w-4 h-4 float-right"></PencilAltIcon>
                                </td>
                            </tr>
                            <tr v-else>
                                <td colspan="8" class="px-6 py-4 whitespace-nowrap text-sm text-gray-900 bg-gray-100">
                                    There are no offices available for your search criteria.
                                </td>
                            </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            <Pagination :links="offices.links"/>
        </div>
    </div>

    <CreateOffice ref="createOfficeSlideout"/>
    <EditBankAccounts ref="editBankAccountsSlideout"/>

</template>


<script>

import Authenticated from "@/Layouts/Authenticated.vue";
import {Head, useForm} from "@inertiajs/vue3";
import Pagination from "@/Components/Pagination.vue";
import SearchFilter from "@/Components/SearchFilter.vue";
import debounce from 'lodash/debounce'
import pickBy from 'lodash/pickBy'
import mapValues from 'lodash/mapValues'
import CreateUser from "@/Components/SlideOuts/CreateUser";
import Button from "@/Components/Button.vue";
import CreateOffice from "@/Components/SlideOuts/CreateOffice.vue";
import EditBankAccounts from "@/Components/SlideOuts/EditBankAccounts.vue";
import {PencilAltIcon, TrashIcon} from "@heroicons/vue/solid";
import {PlusSmIcon as PlusSmIconSolid} from "@heroicons/vue/solid"
export default {
    layout: Authenticated,
    components: {
        CreateOffice,
        EditBankAccounts,
        Button,
        Pagination,
        Head,
        SearchFilter,
        PencilAltIcon,
        TrashIcon,
        PlusSmIconSolid
    },
    props: {
        offices: Object
    },
    data() {
        return {
            form: {
                search: null
            }
        }
    },
    methods: {
        reset() {
            this.form = mapValues(this.form, () => null)
        },
        createOfficeSlideout() {
            this.$refs.createOfficeSlideout.updating = false
            this.$refs.createOfficeSlideout.open();
        },
        editOfficeSlideout(office = null) {
            this.$refs.createOfficeSlideout.updating = office !== null;
            this.$refs.createOfficeSlideout.form.id = office.id ?? null;
            this.$refs.createOfficeSlideout.form.name = office.name ?? null;
            this.$refs.createOfficeSlideout.form.short_name = office.short_name ?? null;
            this.$refs.createOfficeSlideout.form.unit4_code = office.unit4_code ?? null;
            this.$refs.createOfficeSlideout.form.office_manager_id = office.office_manager_id ?? null;
            this.$refs.createOfficeSlideout.form.currency_id = office.currency_id ?? "";
            this.$refs.createOfficeSlideout.form.commission_country_id = office.commission_country_id ?? "";
            this.$refs.createOfficeSlideout.form.telephone = office.telephone ?? null;
            this.$refs.createOfficeSlideout.form.mobile = office.mobile ?? null;
            this.$refs.createOfficeSlideout.form.email = office.email ?? null;
            this.$refs.createOfficeSlideout.form.website = office.website ?? null;
            this.$refs.createOfficeSlideout.form.registered_legal_name = office.registered_legal_name ?? null;
            this.$refs.createOfficeSlideout.form.company_registration_number = office.company_registration_number ?? null;
            this.$refs.createOfficeSlideout.form.vat_number = office.vat_number ?? null;
            this.$refs.createOfficeSlideout.form.has_vat_breakdown = office.has_vat_breakdown ?? 0;
            this.$refs.createOfficeSlideout.form.allow_toms_vat = office.allow_toms_vat ?? 0;
            this.$refs.createOfficeSlideout.form.address.street = office.address.street ?? null;
            this.$refs.createOfficeSlideout.form.address.address_line_1 = office.address.address_line_1 ?? null;
            this.$refs.createOfficeSlideout.form.address.address_line_2 = office.address.address_line_2 ?? null;
            this.$refs.createOfficeSlideout.form.address.address_line_3 = office.address.address_line_3 ?? null;
            this.$refs.createOfficeSlideout.form.address.code = office.address.code ?? null;
            this.$refs.createOfficeSlideout.form.address.city = office.address.city ?? null;
            this.$refs.createOfficeSlideout.form.address.country_id = office.address.country_id ?? "";

            this.$refs.createOfficeSlideout.open();
        },
        editOfficeBankAccountsSlideout(office) {
            this.$refs.editBankAccountsSlideout.updating = true
            this.$refs.editBankAccountsSlideout.open(office);
        }
    },
    watch: {
        form: {
            deep: true,
            handler: debounce(function () {
                this.$inertia.get(route('admin.offices.index'), pickBy(this.form), {preserveState: true})
            }, 500),
        },
    },
}
</script>
